/**
 * Web Stub for @react-native-community/datetimepicker
 * This is a simple replacement for the native DateTimePicker component to use in web environments
 */

import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from "react-native";

// Default export mimicking the structure of @react-native-community/datetimepicker
const DateTimePicker = ({
  value,
  onChange,
  mode = "date",
  display = "default",
  minimumDate,
  maximumDate,
  ...rest
}) => {
  // Web implementation uses HTML input type="date" or type="time"
  const inputType = mode === "date" ? "date" : "time";

  // Format date for input value
  const formatDateForInput = () => {
    if (!value) {
      return "";
    }

    if (mode === "date") {
      // Format as YYYY-MM-DD for date input
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } else {
      // Format as HH:MM for time input
      const hours = String(value.getHours()).padStart(2, "0");
      const minutes = String(value.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const newDate = new Date(value);

    if (mode === "date" && e.target.value) {
      const [year, month, day] = e.target.value.split("-");
      newDate.setFullYear(parseInt(year));
      newDate.setMonth(parseInt(month) - 1);
      newDate.setDate(parseInt(day));
    } else if (mode === "time" && e.target.value) {
      const [hours, minutes] = e.target.value.split(":");
      newDate.setHours(parseInt(hours));
      newDate.setMinutes(parseInt(minutes));
    }

    // Call the provided onChange handler with the new date
    // Mimic the structure of native DateTimePicker's onChange event
    onChange(
      { type: "set", nativeEvent: { timestamp: newDate.getTime() } },
      newDate
    );
  };

  // Convert min/max dates to string format for HTML input
  const getMinDate = () => {
    if (!minimumDate) {
      return undefined;
    }
    const year = minimumDate.getFullYear();
    const month = String(minimumDate.getMonth() + 1).padStart(2, "0");
    const day = String(minimumDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getMaxDate = () => {
    if (!maximumDate) {
      return undefined;
    }
    const year = maximumDate.getFullYear();
    const month = String(maximumDate.getMonth() + 1).padStart(2, "0");
    const day = String(maximumDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <View style={styles.container}>
      <TextInput
        type={inputType}
        value={value ? value.toISOString().split('T')[0] : ''}
        onChange={(e) => onChange(new Date(e.target.value))}
        style={styles.input}
        {...rest}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  input: {
    width: "100%",
    padding: 8,
    fontSize: 16,
    border: "1px solid #ccc",
    borderRadius: 4,
  },
});

// Export constants that are available in the original package
DateTimePicker.defaultProps = {
  mode: "date",
  display: "default",
};

export default DateTimePicker;
